// Per-user progress on per-video objectives.
// Schema mirrors PLAN.md: PK userId, SK youtubeVideoId#objectiveId.
// Two backends: localStorage (mock/offline) and API-backed.

import { getVideoDetail, patchObjective as apiPatchObjective } from "./api-client";
import { TokenProvider } from "./library-store";

async function requireToken(getToken: TokenProvider): Promise<string> {
  const t = await getToken();
  if (!t) throw new Error("Sign in again to continue.");
  return t;
}

export interface ObjectiveProgress {
  checked: boolean;
  checkedAt: number | null; // unix ms
  notes: string;
}

export interface ProgressStore {
  forVideo(youtubeVideoId: string): Promise<Record<string, ObjectiveProgress>>;
  setChecked(youtubeVideoId: string, objectiveId: string, checked: boolean): Promise<void>;
  setNotes(youtubeVideoId: string, objectiveId: string, notes: string): Promise<void>;
}

function storageKey(userSub: string): string {
  return `fretboard.progress.${userSub}`;
}

type AllProgress = Record<string, ObjectiveProgress>; // key: `${youtubeVideoId}#${objectiveId}`

function makeKey(youtubeVideoId: string, objectiveId: string): string {
  return `${youtubeVideoId}#${objectiveId}`;
}

export function createLocalProgressStore(userSub: string): ProgressStore {
  const key = storageKey(userSub);

  function readAll(): AllProgress {
    const raw = localStorage.getItem(key);
    if (!raw) return {};
    try {
      return JSON.parse(raw) as AllProgress;
    } catch {
      return {};
    }
  }

  function writeAll(all: AllProgress): void {
    localStorage.setItem(key, JSON.stringify(all));
  }

  function getOrInit(all: AllProgress, k: string): ObjectiveProgress {
    return all[k] ?? { checked: false, checkedAt: null, notes: "" };
  }

  return {
    async forVideo(youtubeVideoId) {
      const all = readAll();
      const result: Record<string, ObjectiveProgress> = {};
      const prefix = `${youtubeVideoId}#`;
      for (const [k, v] of Object.entries(all)) {
        if (k.startsWith(prefix)) {
          const objectiveId = k.slice(prefix.length);
          result[objectiveId] = v;
        }
      }
      return result;
    },
    async setChecked(youtubeVideoId, objectiveId, checked) {
      const all = readAll();
      const k = makeKey(youtubeVideoId, objectiveId);
      const existing = getOrInit(all, k);
      all[k] = { ...existing, checked, checkedAt: checked ? Date.now() : null };
      writeAll(all);
    },
    async setNotes(youtubeVideoId, objectiveId, notes) {
      const all = readAll();
      const k = makeKey(youtubeVideoId, objectiveId);
      const existing = getOrInit(all, k);
      all[k] = { ...existing, notes };
      writeAll(all);
    },
  };
}

export function createApiProgressStore(getToken: TokenProvider): ProgressStore {
  return {
    async forVideo(videoId) {
      const detail = await getVideoDetail(videoId, await requireToken(getToken));
      return detail?.progress ?? {};
    },
    async setChecked(videoId, objectiveId, checked) {
      await apiPatchObjective(videoId, objectiveId, { checked }, await requireToken(getToken));
    },
    async setNotes(videoId, objectiveId, notes) {
      await apiPatchObjective(videoId, objectiveId, { notes }, await requireToken(getToken));
    },
  };
}

export function formatTimestamp(seconds: number | null): string {
  if (seconds == null) return "";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

// Accepts "m:ss", "mm:ss", "h:mm:ss", or a plain seconds integer. Empty string
// returns null (user is clearing the timestamp).
export function parseTimestamp(input: string): number | null | "invalid" {
  const trimmed = input.trim();
  if (!trimmed) return null;
  if (/^\d+$/.test(trimmed)) return Number(trimmed);
  const parts = trimmed.split(":").map((p) => p.trim());
  if (parts.length < 2 || parts.length > 3) return "invalid";
  if (!parts.every((p) => /^\d+$/.test(p))) return "invalid";
  const nums = parts.map(Number);
  if (parts.length === 2) {
    const [m, s] = nums;
    if (s >= 60) return "invalid";
    return m * 60 + s;
  }
  const [h, m, s] = nums;
  if (m >= 60 || s >= 60) return "invalid";
  return h * 3600 + m * 60 + s;
}
